import { useMemo } from 'react'
import { CategoryScale, Chart as ChartJS, ChartData, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'next-i18next/pages'
import useWindowSize from 'hooks/useWindowSize'
import { useGetLanguage } from 'hooks/useGetLanguage'
import styles from '../Charts.module.scss'
import legendStyles from './ChartLegend.module.scss'
import { getPriceHistoryChartOptions } from './chartjs.config'
import { MONTH_KEY_FORMAT, MONTHS_IN_YEAR, PRICE_HISTORY_TIMEZONE } from './constants'
import { PriceHistoryChartProps } from './types'

dayjs.extend(utc)
dayjs.extend(timezone)

ChartJS.register(CategoryScale, PointElement, LinearScale, Tooltip, LineElement)

const PriceHistoryChart = ({ activeProduct, data, year }: PriceHistoryChartProps) => {
  // i18n
  const { t } = useTranslation('pricelists')
  const language = useGetLanguage()

  // Window size
  const { isTablet } = useWindowSize()

  // Memoized constants
  const months = useMemo(
    () => Array.from({ length: MONTHS_IN_YEAR }, (_, month) => dayjs(`${year}-01-01`).add(month, 'month').locale(language)),
    [language, year]
  )

  const chartData = useMemo<ChartData<'line', (number | null)[], string>>(() => {
    // Months after the current one only hold estimated prices
    const currentMonthKey = dayjs().tz(PRICE_HISTORY_TIMEZONE).format(MONTH_KEY_FORMAT)
    const pricesByMonth = new Map(data.map(({ date, price }) => [dayjs(date).tz(PRICE_HISTORY_TIMEZONE).format(MONTH_KEY_FORMAT), price]))

    return {
      labels: months.map((month) => month.format('MMM')),
      datasets: [
        {
          data: months.map((month) => {
            const monthKey = month.format(MONTH_KEY_FORMAT)
            return monthKey <= currentMonthKey ? (pricesByMonth.get(monthKey) ?? null) : null
          })
        }
      ]
    }
  }, [data, months])

  const options = useMemo(() => getPriceHistoryChartOptions(months, language, isTablet), [isTablet, language, months])

  return (
    <>
      <div className={styles['chart-wrapper']}>
        <Line options={options} data={chartData} className={styles.chart} />
      </div>

      {/* LEGEND */}
      <ul className={legendStyles.legend}>
        <li className={legendStyles['legend-item']}>
          <div className={legendStyles['line-indicator']} />
          <span>{t(`chart.labels.${activeProduct}.knownPrice`)}</span>
        </li>
      </ul>
    </>
  )
}

export default PriceHistoryChart
